import React from "react";
import {DATE_FORMAT, TIME_FORMAT} from "../../services/timezone-service";
import {getFormattedDate, FALLBACK_FORMAT_PARAMS} from "../../utilities/timezone-utilities";

/**
 * Enum defining what parts of date/time to display
 */
export enum DATETIME_VIEW {
    FULL,
    DATE_ONLY,
    MONTH_YEAR_ONLY,
    TIME_ONLY
}

/**
 * Props for DateTimeV2 component.
 */
export interface IDateTimeV2Props {
    /** UTC date string (ISO "2025-01-04T10:30:00Z" or DB format "2025-01-04 10:30:00") - REQUIRED */
    utcDateString: string;
    
    /** What to display: full date+time, date only, time only, or month+year */
    dateTimeView?: DATETIME_VIEW;
    
    /** OPTIONAL: Override merchant timezone (e.g., "UTC" for billing transactions) */
    timeZone?: string;
    
    /** OPTIONAL: Override date format (defaults to merchant setting) */
    dateFormat?: DATE_FORMAT;
    
    /** OPTIONAL: Override time format (defaults to merchant setting) */
    timeFormat?: TIME_FORMAT;
}

/**
 * DateTimeV2 component - displays formatted dates with consistent timezone handling.
 * 
 * @component
 * @remarks
 * Pure presentation component - no feature flags, no store dependencies.
 * All formatting parameters are provided via props or fall back to unified defaults.
 * 
 * Formatting parameter priority:
 * 1. Explicit props (timeZone, dateFormat, timeFormat)
 * 2. Fallback defaults (UTC, DATE_FORMAT.FIRST, TIME_FORMAT.AM_PM)
 *
 * Usage patterns:
 * - Use useDateTimeFormattingParams hook for automatic merchant settings + fallback
 * - Pass props directly for specific overrides (e.g., billing always in UTC)
 * - Leave props empty to use fallback defaults
 * 
 * Contract:
 * - Always accepts UTC date strings as input (ISO or DB format)
 * - No automatic loading of merchant settings - caller is responsible
 * - Pure rendering component
 * 
 * @example
 * With useDateTimeFormattingParams hook (recommended)
 * ```tsx
 * const params = useDateTimeFormattingParams();
 * <DateTimeV2 {...params} utcDateString="2025-01-04T10:30:00Z" dateTimeView={DATETIME_VIEW.FULL} />
 * ```
 * 
 * @example
 * With explicit override (e.g., billing transactions always in UTC)
 * ```tsx
 * const params = useDateTimeFormattingParams({
 *   timeZone: 'UTC',
 *   dateFormat: DATE_FORMAT.FOURTH,
 *   timeFormat: TIME_FORMAT.TWENTY_FOUR
 * });
 * <DateTimeV2 {...params} utcDateString={transaction.created_at} />
 * ```
 *
 * @example
 * Fallback to defaults (no hook, no props)
 * ```tsx
 * <DateTimeV2 utcDateString="2025-01-04T10:30:00Z" />
 * ```
 */
const DateTimeV2: React.FC<IDateTimeV2Props> = ({
    utcDateString,
    timeZone,
    dateTimeView = DATETIME_VIEW.DATE_ONLY,
    dateFormat,
    timeFormat,
}) => {
    /** Validate input */
    if (!utcDateString) {
        return <></>;
    }

    /** Apply fallback defaults for any missing formatting parameters */
    const finalTimeZone = timeZone ?? FALLBACK_FORMAT_PARAMS.timeZone;
    const finalDateFormat = dateFormat ?? FALLBACK_FORMAT_PARAMS.dateFormat;
    const finalTimeFormat = timeFormat ?? FALLBACK_FORMAT_PARAMS.timeFormat;
    
    const formattedDate = getFormattedDate({
        utcDateString,
        dateFormat: finalDateFormat,
        timeFormat: finalTimeFormat,
        timeZone: finalTimeZone,
        dateTimeView
    });
    
    return <>{formattedDate}</>;
};

export default DateTimeV2;
